/**
 * Client 360's **Overview** tab — the record's details and its history, as two
 * section cards (C14 §10.6 item 3; T-6.14). Split out of `index.tsx` in Wave
 * 15B; what it shows is what the old *Record* and *History* panels showed.
 */

import { Badge, DescriptionList, Detail, formatDateTime } from '@fiveox/ui'
import { History, IdCard } from 'lucide-react'

import { RecordSection } from '../../components/page'
import {
  ACTOR_LABELS,
  type ClientRecord,
  type HistoryEntry,
  historyTone,
  panDisplay,
} from '../../lib/api/clients'

/** Address, city, state and PIN as one line, skipping the parts that are not
 *  set — so a client with a city and nothing else reads as the city rather
 *  than as three commas. */
export function addressLine(client: {
  address: string | null
  city: string | null
  state: string | null
  pincode: string | null
}): string | null {
  const parts = [client.address, client.city, client.state, client.pincode].filter(Boolean)
  return parts.length > 0 ? parts.join(', ') : null
}

export function OverviewTab({ client, history }: { client: ClientRecord; history: HistoryEntry[] }) {
  return (
    <div className="grid min-w-0 gap-6 lg:grid-cols-5">
      <div className="min-w-0 lg:col-span-3">
        <RecordSection
          id="client-details"
          icon={<IdCard size={18} aria-hidden />}
          title="Details"
          description="Contact details, identifiers and what this client holds. Edit changes them."
        >
          <DescriptionList>
            <Detail
              label="PAN"
              value={<span className="font-mono">{panDisplay(client.pan)}</span>}
            />
            <Detail
              label="GSTIN"
              value={<span className="font-mono">{client.gstin ?? '—'}</span>}
            />
            <Detail label="Email" value={client.email} />
            <Detail label="Mobile" value={client.mobile} />
            {/* w7a — the address is editable (§3.1) and so it is readable.
                A field a screen writes and never shows is a field the firm
                cannot tell it saved. One line, because it is one place. */}
            <Detail label="Address" value={addressLine(client)} wide />
            {client.registrations.length > 0 && (
              <Detail
                label="Registrations held"
                wide
                value={
                  <ul className="space-y-1">
                    {client.registrations.map((registration) => (
                      <li key={`${registration.kind}-${registration.value}`}>
                        <span className="text-muted-foreground">
                          {registration.label ?? registration.kind.toUpperCase()}:
                        </span>{' '}
                        <span className="font-mono">{registration.value}</span>
                      </li>
                    ))}
                  </ul>
                }
              />
            )}
            {client.tags.length > 0 && (
              <Detail
                label="Tags"
                wide
                value={
                  <span className="flex flex-wrap gap-1">
                    {client.tags.map((tag) => (
                      <Badge key={tag} size="sm">
                        {tag}
                      </Badge>
                    ))}
                  </span>
                }
              />
            )}
          </DescriptionList>
        </RecordSection>
      </div>

      <div className="min-w-0 lg:col-span-2">
        <RecordSection
          id="client-history"
          icon={<History size={18} aria-hidden />}
          title="History"
          description="What has happened to this record."
        >
          {history.length === 0 ? (
            <p className="text-muted-foreground text-sm">Nothing yet.</p>
          ) : (
            <ol className="space-y-3">
              {history.map((entry) => (
                <li key={entry.id} className="border-border border-l-2 pl-3">
                  <p
                    className={`text-foreground text-sm ${
                      historyTone(entry.kind) === 'converted' ? 'font-medium' : ''
                    }`}
                  >
                    {entry.summary}
                  </p>
                  <p className="text-muted-foreground mt-0.5 flex flex-wrap items-center gap-2 text-xs">
                    {formatDateTime(entry.at)}
                    {ACTOR_LABELS[entry.actor_kind] ? (
                      <Badge size="sm">{ACTOR_LABELS[entry.actor_kind]}</Badge>
                    ) : null}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </RecordSection>
      </div>
    </div>
  )
}
